import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router';
import { Button, Dropdown } from 'antd';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '@redux/hook';
import type { RootState } from '@redux/store';
import { DownOutlined, UpOutlined } from '@ant-design/icons';

import { logout } from '@redux/features/GeneralReducer';
import { routes } from '@routes/index';
import { getLastLogin, removeLastLogin } from '@common/storage';
import { MenuItem, SubMenuItem } from './interface';
import { IHeaderProps } from '@common/interface';
import { PageId } from '@common/constants';
import Logo from '@assets/images/logo-main.png';

const Header = (props: IHeaderProps) => {
	const { pageInfo } = props;
	const navigate = useNavigate();
	const dispatch = useAppDispatch();
	const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
	const [openSubmenu, setOpenSubmenu] = useState<string[]>([]);
	const { isLogin } = useSelector((state: RootState) => state.general);
	const { name } = useSelector((state: RootState) => state.user);
	const [userMenuOpen, setUserMenuOpen] = useState(false);

	useEffect(() => {
		const TWO_HOURS = 2 * 60 * 60 * 1000;
		const lastLogin = getLastLogin();
		if (Date.now() - lastLogin > TWO_HOURS) {
			dispatch(logout());
			removeLastLogin();
		}
	}, []);

	useEffect(() => {
		const handleResize = () => {
			if (window.innerWidth > 1024 && isMobileMenuOpen) {
				setIsMobileMenuOpen(false);
				setOpenSubmenu([]);
			}
		};

		if (isMobileMenuOpen) {
			document.body.classList.add('no-scroll');
		} else {
			document.body.classList.remove('no-scroll');
		}

		window.addEventListener('resize', handleResize);

		return () => {
			document.body.classList.remove('no-scroll');
			window.removeEventListener('resize', handleResize);
		};
	}, [isMobileMenuOpen]);

	const toggleMobileMenu = () => {
		setIsMobileMenuOpen(!isMobileMenuOpen);
		if (!isMobileMenuOpen) {
			setOpenSubmenu([]);
		}
	};

	const toggleSubmenu = (id: string) => {
		if (openSubmenu.includes(id)) {
			setOpenSubmenu(prev => prev.filter(item => item !== id));
		} else {
			setOpenSubmenu(prev => [...prev, id]);
		}
	};

	const onJoinUs = () => {
		setIsMobileMenuOpen(false);
		navigate('/join-us');
	};

	const onLogin = () => {
		navigate('/login');
	};

	const onLogout = () => {
		dispatch(logout());
		setIsMobileMenuOpen(false);
	};

	const rawMenuItems: MenuItem[] = [
		{ id: PageId.Homepage },
		// { id: PageId.ActiveEvent1 },
		// { id: PageId.ActiveEvent2 },
		{
			id: PageId.AboutUs,
			subMenu: [
				{
					id: PageId.MissionVision,
					label: 'Vission & Mission',
				},
				{
					id: PageId.BoardOfAdvisors,
					label: 'Board of Advisors',
				},
				{
					id: PageId.ExecutiveCouncil,
					label: 'Executive Council Office Bearers',
				},
				{
					id: PageId.IndustrialExpertAdvisory,
					label: 'Industrial Expert Advisory Committee',
				},
				{
					id: PageId.TAAConstitution,
					label: 'Constitution',
					link: `${process.env.REACT_APP_FILE_SERVER_URL}/docs/Perlembagaan Pertubuhan - PPM-012-14-03022012 (24.7.2024).pdf`,
					target: '_blank',
					rel: 'noreferrer',
				},
			],
		},
		{
			id: PageId.Events,
			subMenu: [
				{
					id: PageId.EventsGallery,
					label: 'Event Gallery',
				},
				{
					id: PageId.Video,
					label: 'Event Videos',
				},
			],
		},
		{
			id: PageId.News,
			subMenu: [
				{
					id: PageId.NewsRelease,
					label: 'Event News Release',
				},
				{
					id: PageId.NewsClippings,
					label: 'Event News Clippings',
				},
				{
					id: PageId.AlumniNewsClippings,
					label: 'Alumni News Clippings',
				},
				{
					id: PageId.CallToReform,
					label: 'Call To Reform',
				},
				{
					id: PageId.TAAEducationTrust,
					label: 'TAA Education Trust',
				},
			],
		},
		{
			id: PageId.ConstitutionFinancials,
			label: 'Notices & Reports',
			subMenu: [
				{
					id: 'Financial Report 2025',
					label: 'Financial Report 2025',
					link: `${process.env.REACT_APP_FILE_SERVER_URL}/docs/Constitution_Financials_2025.pdf`,
					target: '_blank',
					rel: 'noreferrer',
				},
				{
					id: 'Financial Report 2024',
					label: 'Financial Report 2024',
					link: `${process.env.REACT_APP_FILE_SERVER_URL}/docs/Constitution_Financials_2024.pdf`,
					target: '_blank',
					rel: 'noreferrer',
				},
				{
					id: 'System Migration Notice',
					label: 'System Migration Notice',
					link: `${process.env.REACT_APP_FILE_SERVER_URL}/docs/System_Migration_Notice.pdf`,
					target: '_blank',
					rel: 'noreferrer',
				},
			],
		},
		{
			id: PageId.Membership,
			subMenu: [
				{
					id: PageId.MembershipPolicy,
					label: 'Membership Policy',
				},
				{
					id: PageId.JoinUs,
					label: 'Join Us',
				},
				{
					id: PageId.TAAPrivacyPolicy,
					label: 'TAA Privacy Policy',
				},
			],
		},
		{
			id: PageId.RegionalCharter,
			label: 'Charters',
			subMenu: [
				{
					id: PageId.SouthCharter,
					label: 'South Charter',
				},
				{
					id: PageId.TCCI,
					label: 'TCCI',
				},
			],
		},
		// {
		// 	id: PageId.MakeAGift,
		// 	label: 'Make A Gift',
		// 	link: `${process.env.REACT_APP_FILE_SERVER_URL}/images/layout/DonationAppealLetter.jpg`,
		// 	target: '_blank',
		// 	rel: 'noreferrer',
		// },
		// Only include UserProfile when user is logged in
		...(isLogin ? [{ id: PageId.UserProfile }] : []),
	];

	const mapMenuItem = (item: MenuItem | SubMenuItem): MenuItem | SubMenuItem => {
		const route = routes.find(route => route.page.selfId === item.id);
		const mappedItem: any = {
			...item,
			header: route?.header || item.header,
			label: route?.title || item.label || item.id,
			link: route?.path || item.link || undefined,
			rel: item.rel || undefined,
			target: item.target || undefined,
		};

		if (item.subMenu) {
			mappedItem.subMenu = item.subMenu.map(
				(subItem: SubMenuItem) => mapMenuItem(subItem) as SubMenuItem
			);
		}

		return mappedItem;
	};

	const menuItems = rawMenuItems.map(item => mapMenuItem(item) as MenuItem);

	const userMenuItems = [
		{
			key: 'profile',
			label: 'Profile',
			onClick: () => {
				navigate('/user-profile');
			},
		},
		{
			key: 'logout',
			label: <span style={{ color: 'red' }}>Logout</span>,
			onClick: onLogout,
		},
	];

	const renderSubMenuItems = (items: SubMenuItem[]) => {
		return items.map(subItem => {
			const hasSubMenu = subItem.subMenu && subItem.subMenu.length > 0;
			const isOpen = openSubmenu.includes(subItem.id as string);

			if (hasSubMenu) {
				return (
					<div key={subItem.id} className="dropdown-item-group">
						<div
							className="dropdown-item parent"
							onClick={e => {
								e.preventDefault();
								e.stopPropagation();
								toggleSubmenu(subItem.id as string);
							}}
							style={{
								display: 'flex',
								justifyContent: 'space-between',
								alignItems: 'center',
								cursor: 'pointer',
							}}
						>
							<span>{subItem.label}</span>
							<span
								className="submenu-arrow"
								style={{
									transform: isOpen ? 'rotate(180deg)' : 'rotate(-90deg)',
									display: 'inline-block',
									marginLeft: '10px',
								}}
							>
								▼
							</span>
						</div>
						<div className={`dropdown-menu ${isOpen ? 'show' : ''} nested`}>
							{renderSubMenuItems(subItem.subMenu!)}
						</div>
					</div>
				);
			}

			return (
				<a
					key={subItem.id}
					href={subItem.link || '#'}
					rel={subItem.rel}
					target={subItem.target}
					className="dropdown-item"
					onClick={() => {
						setIsMobileMenuOpen(false);
						setOpenSubmenu([]);
					}}
				>
					{subItem.label}
				</a>
			);
		});
	};

	return (
		<>
			<div className="header">
				<div className="header-container">
					<div className="header-logo">
						<img src={Logo} alt="TARCian Alumni Association" onClick={() => navigate('/')} />
					</div>

					<button className="mobile-menu-button" onClick={toggleMobileMenu}>
						<span className={`hamburger ${isMobileMenuOpen ? 'open' : ''}`}></span>
					</button>

					<nav className={`header-nav ${isMobileMenuOpen ? 'mobile-open' : ''}`}>
						<div className="mobile-login-button-container">
							{isMobileMenuOpen && isLogin && (
								<div style={{ position: 'absolute', top: 30, left: 20 }}>
									<span style={{ fontWeight: 700 }}>Hi, {name}</span>
								</div>
							)}
						</div>
						<div className="header-nav-container">
							{menuItems.map((item, index) => (
								<div
									key={index}
									className={`nav-item ${openSubmenu.includes(item.id) ? 'active' : ''}`}
								>
									<div className="nav-link-container">
										<a
											href={item.link || '#'}
											rel={item.rel}
											target={item.target}
											className={`nav-link ${item.id === pageInfo.parentId || item.id === pageInfo.selfId ? 'active' : ''}`}
											onClick={() => setIsMobileMenuOpen(false)}
										>
											{item.label}
										</a>
										{item.subMenu && (
											<button
												className="submenu-toggle"
												onClick={e => {
													e.preventDefault();
													toggleSubmenu(item.id);
												}}
											>
												<span className="submenu-arrow">▼</span>
											</button>
										)}
									</div>
									{item.subMenu && (
										<div className={`dropdown-menu ${openSubmenu.includes(item.id) ? 'show' : ''}`}>
											{renderSubMenuItems(item.subMenu)}
										</div>
									)}
								</div>
							))}
							<div className="mobile-login-button-container">
								<Button
									type="primary"
									className="login-button"
									onClick={onJoinUs}
									style={{ width: '100%' }}
								>
									Join Us
								</Button>
								{isMobileMenuOpen && isLogin ? (
									<div style={{ marginTop: 5 }}>
										<Button
											type="link"
											style={{ color: 'red', padding: 0, fontSize: 16, fontWeight: 500 }}
											onClick={onLogout}
										>
											Logout
										</Button>
									</div>
								) : (
									<div className="login-container">
										<Button
											type="primary"
											className="login-button"
											onClick={onLogin}
											style={{ width: '100%' }}
										>
											Login
										</Button>
									</div>
								)}
							</div>
						</div>
					</nav>
					{isLogin ? (
						<div className="logout-container">
							<div>
								<Dropdown
									menu={{ items: userMenuItems }}
									placement="bottomRight"
									trigger={['click']}
									onOpenChange={() => setUserMenuOpen(!userMenuOpen)}
								>
									<div style={{ fontWeight: 700 }}>
										<span style={{ marginRight: 10 }}>{name}</span>
										{userMenuOpen ? <UpOutlined /> : <DownOutlined />}
									</div>
								</Dropdown>
							</div>
						</div>
					) : (
						<div className="login-button-container">
							<Button type="primary" className="login-button" onClick={() => navigate('/join-us')}>
								Join Us
							</Button>
							<Button
								type="primary"
								className="login-button"
								onClick={onLogin}
								style={{ marginLeft: 8 }}
							>
								Login
							</Button>
						</div>
					)}
				</div>
			</div>
			{/* {currentPage && currentPage.id !== PageId.Homepage && (
				<div className="page-title-banner">
					<div className="page-title-container">
						<h1>{currentPage.header || currentPage.label}</h1>
					</div>
				</div>
			)} */}
		</>
	);
};

export default Header;
